/**
 * Shared HTML email scaffolding: outer wrapper, table rows, section headers.
 * These functions are used by most generateXxxEmailHtml templates and must
 * preserve their exact output so existing emails render identically.
 */

/**
 * Base email wrapper with modern styling
 * @param {string} content - Inner HTML content
 * @param {string} title - Email title
 * @returns {string} Complete HTML email
 */
export function emailWrapper(content, title = '') {
  return `
<!DOCTYPE html>
<html lang="he" dir="rtl">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${title}</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Rubik:wght@400;500;600;700&display=swap');
    @media only screen and (max-width: 620px) {
      .email-outer-td { padding: 0 !important; }
      .email-header-td { border-radius: 0 !important; }
      .email-content-td { border-radius: 0 !important; }
    }
  </style>
</head>
<body style="margin: 0; padding: 0; background-color: #f8fafc; font-family: 'Rubik', Arial, sans-serif; direction: rtl;">
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background-color: #f8fafc;">
    <tr>
      <td align="center" class="email-outer-td" style="padding: 40px 20px;">
        <table role="presentation" width="600" cellspacing="0" cellpadding="0" style="max-width: 600px; width: 100%;">
          <!-- Header -->
          <tr>
            <td class="email-header-td" style="background: linear-gradient(135deg, #1e293b 0%, #334155 100%); padding: 20px 16px; border-radius: 16px 16px 0 0; text-align: center;">
              <h1 style="margin: 0; color: #ffffff; font-size: 24px; font-weight: 700;">
                GoFood
              </h1>
            </td>
          </tr>
          <!-- Content -->
          <tr>
            <td class="email-content-td" style="background-color: #ffffff; padding: 24px 16px; border-radius: 0 0 16px 16px; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);">
              ${content}
            </td>
          </tr>
          <!-- Footer -->
          <tr>
            <td style="padding: 24px; text-align: center;">
              <p style="margin: 0; color: #64748b; font-size: 12px;">
                הודעה זו נשלחה באופן אוטומטי ממערכת GoFood
              </p>
              <p style="margin: 8px 0 0; color: #94a3b8; font-size: 11px;">
                © ${new Date().getFullYear()} GoFood. כל הזכויות שמורות.
              </p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>
  `.trim()
}

/**
 * Create a styled data row for tables
 * @param {string} label - Row label
 * @param {string} value - Row value
 * @param {boolean} isAlt - Alternate row styling
 * @returns {string} HTML table row
 */
export function dataRow(label, value, isAlt = false) {
  const bgColor = isAlt ? '#f8fafc' : '#ffffff'
  return `
    <tr>
      <th style="background-color: ${bgColor}; padding: 12px 16px; text-align: right; font-weight: 600; color: #334155; border-bottom: 1px solid #e2e8f0; width: 40%;">
        ${label}
      </th>
      <td style="background-color: ${bgColor}; padding: 12px 16px; text-align: right; color: #475569; border-bottom: 1px solid #e2e8f0;">
        ${value || '—'}
      </td>
    </tr>
  `
}

/**
 * Create an image row for tables
 * @param {string} label - Row label
 * @param {string} imageUrl - Image URL
 * @param {boolean} isAlt - Alternate row styling
 * @returns {string} HTML table row with image
 */
export function imageRow(label, imageUrl, isAlt = false) {
  if (!imageUrl) return ''
  const bgColor = isAlt ? '#f8fafc' : '#ffffff'
  return `
    <tr>
      <th style="background-color: ${bgColor}; padding: 12px 16px; text-align: right; font-weight: 600; color: #334155; border-bottom: 1px solid #e2e8f0; vertical-align: top;">
        ${label}
      </th>
      <td style="background-color: ${bgColor}; padding: 12px 16px; text-align: right; border-bottom: 1px solid #e2e8f0;">
        <img src="${imageUrl}" alt="${label}" style="max-width: 200px; max-height: 150px; border-radius: 8px; border: 1px solid #e2e8f0;" />
      </td>
    </tr>
  `
}

/**
 * Section header for grouping data
 * @param {string} title - Section title
 * @param {string} icon - Optional emoji icon
 * @returns {string} HTML section header
 */
export function sectionHeader(title, icon = '') {
  return `
    <tr>
      <td colspan="2" style="background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%); padding: 12px 16px; font-weight: 600; color: #283548; font-size: 14px; border-bottom: 2px solid #283548;">
        ${icon} ${title}
      </td>
    </tr>
  `
}
